import { useQueryState } from '@experiences/util';
import type { PropsWithChildren } from 'react';
import React, {
    createContext,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { useSelector } from 'react-redux';
import useSWR from 'swr';

import { getFriendlyName } from '../../../common/constants/ServicesMapping';
import { useOrganizationName } from '../../../common/hooks/useOrganizationName';
import { useTenantIdContext } from '../../../common/providers/TenantIdProvider';
import { getEffectiveAccess } from '../../../services/centralizedaccess/CentralizedAccessService';
import type { Folder } from '../../../services/orchestrator/OrchFolderService';
import { accountGlobalId } from '../../../store/selectors';
import type {
    CheckAccessLevel,
    ICheckAccessTarget,
} from './CheckAccessScope';
import { buildScopeIdentifier } from './CheckAccessScope';

export interface ICheckAccessPrincipal {
    identifier: string;
    displayName: string;
    email?: string;
}

interface ICheckAccessContext {
    /** The top-level scope (organization or tenant) this Check Access experience is rendered for */
    target: ICheckAccessTarget;
    principal?: ICheckAccessPrincipal;
    setPrincipal: React.Dispatch<React.SetStateAction<ICheckAccessPrincipal | undefined>>;
    roleId?: string;
    setRoleId: React.Dispatch<React.SetStateAction<string | undefined>>;
    selectedScope?: string;
    setSelectedScope: React.Dispatch<React.SetStateAction<string | undefined>>;
    scopedServiceName?: string;
    scopedServiceDisplayName?: string;
    selectedFolder?: Folder;
    setSelectedFolder: React.Dispatch<React.SetStateAction<Folder | undefined>>;
}

const CheckAccessContext = createContext<ICheckAccessContext>({} as ICheckAccessContext);

export const useCheckAccessContext = () => useContext(CheckAccessContext);

export const CheckAccessProvider: React.FC<PropsWithChildren<{ level?: CheckAccessLevel }>> = ({
    children, level = 'Tenant',
}) => {
    const {
        services, tenantId, tenantName,
    } = useTenantIdContext();
    const partitionGlobalId = useSelector(accountGlobalId);
    const organizationName = useOrganizationName();

    const target: ICheckAccessTarget = useMemo(() => level === 'Organization'
        ? {
            level,
            id: partitionGlobalId,
            name: organizationName,
        }
        : {
            level,
            id: tenantId,
            name: tenantName,
        }, [ level, partitionGlobalId, organizationName, tenantId, tenantName ]);

    // Selected principal
    const [ principal, setPrincipal ] = useQueryState<ICheckAccessPrincipal | undefined>('principal', undefined, undefined, true);

    // Selected Treeview state
    const [ selectedScope, setSelectedScope ] = useQueryState<string | undefined>('scope', undefined, undefined, true);

    // Selected folder
    const [ selectedFolder, setSelectedFolder ] = useState<Folder | undefined>();

    // Selected role id
    const [ roleId, setRoleId ] = useQueryState<string | undefined>('roleId', undefined, undefined, true);

    // Scoped ServiceName for permissions call, undefined when tenant is selected
    const [ scopedServiceName, setScopedServiceName ] = useState<string>();
    const [ scopedServiceDisplayName, setScopedServiceDisplayName ] = useState<string>();

    // This call is used to get the effective services at the current scope for the selected principal
    // The actual effective permissions here are ignored and called somewhere else with proper pagination
    const { data } = useSWR(
        principal && target.id ? {
            url: `/pap_/api/geteffectiveaccess`,
            pagination: {
                top: 10,
                skip: 0,
            },
            payload: {
                SecurityPrincipalId: principal.identifier,
                ScopeIdentifier: buildScopeIdentifier(target),
            },
        } : null,
        getEffectiveAccess,
    );

    // Set default selected scope to the top-level scope id (tenant or organization) once the principal is set
    useEffect(() => {
        if (principal) {
            setSelectedScope(prev => prev ?? target.id);
        }
    }, [ setSelectedScope, target.id, principal ]);

    useEffect(() => {
        if (!selectedScope) {
            return;
        }

        const matchedService = services?.find(service => service.id === selectedScope);
        const matchedServiceFromData = matchedService?.serviceType
            ? data?.grantedServicesMetadata?.find(service => service.serviceType === matchedService.serviceType)
            : undefined;

        const serviceType = matchedService?.serviceType;
        setScopedServiceName(matchedServiceFromData?.serviceName ?? (serviceType ? getFriendlyName(serviceType).replace(' ', '') : undefined));
        setScopedServiceDisplayName(matchedServiceFromData?.serviceDisplayName ?? (serviceType ? getFriendlyName(serviceType) : undefined));
    }, [ services, selectedScope, data?.grantedServicesMetadata ]);

    return <CheckAccessContext.Provider value={{
        target,
        principal,
        setPrincipal,
        roleId,
        setRoleId,
        selectedScope,
        setSelectedScope,
        scopedServiceName,
        scopedServiceDisplayName,
        selectedFolder,
        setSelectedFolder,
    }}>
        {children}
    </CheckAccessContext.Provider>;
};
